// Map pins — each type resolves to a pair of SVG files in /public/assets/:
//   withBackground=true  → map_pin_<type>.svg     (teardrop pin with glyph)
//   withBackground=false → map_pin_<type>_nobg.svg (bare icon only)
//
// Base size matches WeTrack: baseSizeMapPins = 20px, baseSizeMapPinsLarge = 28px (selected).
// Rendered inside an SVG <g> with translate(x,y) scale(s) translate(-28,-47)
// so the pin tip aligns to (x, y) on the map.

import { useState, useEffect, useMemo } from "react";
import type { MouseEvent } from "react";
import type { MapObject } from '../lib/types';
import { pinDefaultColor } from '../lib/mapPinTypes';
import { DEFAULT_CUSTOM_PIN_SYMBOL } from '../lib/pinTypeCatalog';
import { pinAnchor, pinColourOverride, recolourPinSvg } from '../lib/pinArtwork';

export function useDPRScale(): number {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    setScale(Math.min(1, window.devicePixelRatio / 3));
  }, []);
  return scale;
}

const rawSvgCache = new Map<string, string>();
const recolouredCache = new Map<string, string>();

/** The markup at `href`, or null until it has been fetched. */
function useSvgText(href: string): string | null {
  // The fetched markup is kept PAIRED with the href it came from. Holding the
  // text alone made toggling `withBackground` show the wrong drawing until a
  // full page reload: on the render right after `href` flips, state still held
  // the previous file's markup, so the memo built a data URI out of the OLD
  // drawing and cached it under the NEW href's key. That poisoned the
  // module-level `recolouredCache` — every later render hit the bad entry, and
  // only a reload (which discards the Maps) recovered. Comparing
  // `raw.href === href` makes stale markup unusable instead of cacheable.
  const [raw, setRaw] = useState<{ href: string; text: string } | null>(() => {
    const cached = rawSvgCache.get(href);
    return cached === undefined ? null : { href, text: cached };
  });

  useEffect(() => {
    const cached = rawSvgCache.get(href);
    if (cached !== undefined) {
      // Only when it would actually change: on a warm cache the initializer has
      // already set this, and a fresh object defeats React's bail-out (it could
      // bail when this state was a string), costing every pin on the map an
      // extra render and useMemo pass on mount.
      setRaw((prev) => (prev?.href === href && prev.text === cached ? prev : { href, text: cached }));
      return;
    }
    let cancelled = false;
    fetch(href)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        rawSvgCache.set(href, text);
        setRaw({ href, text });
      })
      .catch((err) => {
        if (cancelled) return;
        // Deliberately NOT cached. An empty body would satisfy the
        // `cached !== undefined` check above forever, so one dropped request —
        // a connectivity blip mid-load — would permanently stop this asset
        // being recoloured: the pin would fall back to its baked artwork
        // colours and silently lose the colour its owner chose, until a full
        // page reload. Letting the next mount retry costs one refetch for a
        // genuinely missing file, and the asset audit already guarantees none
        // are missing.
        //
        // Settling local state (without caching) still stops THIS instance
        // refetching in a loop: the effect only re-runs when `href` changes.
        // An empty text falls through to the plain href below, so the browser
        // renders the asset itself rather than a blank data URI.
        setRaw({ href, text: "" });
        if (process.env.NODE_ENV !== "production") {
          console.error(`[MapPin] Failed to fetch SVG: ${href}`, err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [href]);

  return raw?.href === href ? raw.text : null;
}

/**
 * The image to draw for a pin: the asset at `href`, recoloured to `color` when
 * the pin carries a colour of its own. `designHref` is the type's
 * with-background drawing, against which `pinColourOverride` tells a real
 * override from the type's own colour written down.
 */
function useRecolouredPinHref(href: string, color: string | undefined, designHref: string = href): string {
  const text = useSvgText(href);
  const designText = useSvgText(designHref);

  return useMemo(() => {
    if (!text) return href;
    // Until the design drawing has loaded there is no telling a type's own
    // colour from a real override; the plain asset is the right guess for the
    // pins that store their own colour, and the wait is one cached fetch.
    if (color && designText === null) return href;
    const override = designText ? pinColourOverride(color, designText) : undefined;
    if (!override) {
      const key = `${href}::no-color`;
      const cached = recolouredCache.get(key);
      if (cached) return cached;
      const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(text)}`;
      recolouredCache.set(key, dataUri);
      return dataUri;
    }
    const key = `${href}::${override.toLowerCase()}`;
    const cached = recolouredCache.get(key);
    if (cached) return cached;
    const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(recolourPinSvg(text, override))}`;
    recolouredCache.set(key, dataUri);
    return dataUri;
  }, [href, color, text, designText]);
}

/** @deprecated — kept for non-component callers. Always returns 1 during SSR. */
export const DPR_SCALE = typeof window !== "undefined" ? Math.min(1, window.devicePixelRatio / 3) : 1;

export type PinKind = string;

/**
 * Every id that has its own artwork under /public/assets. Anything else draws
 * `other`, matching mobile's PinIconHelper fallback. Keep in step with the
 * asset folder; the asset audit test checks both variants for each entry.
 */
export const KNOWN_ART: ReadonlySet<string> = new Set([
  'tower', 'stand', 'pin', 'camera', 'camera_shared', 'parking', 'gathering_place',
  'road_barrier', 'bait', 'trap', 'bridge', 'fireplace', 'crossing', 'salt_stone',
  'feeding_grounds', 'target', 'mushroom', 'berries', 'other',
  'moose', 'red_deer', 'roe_deer', 'fallow_deer', 'white_tailed_deer', 'reindeer',
  'wild_boar', 'bear', 'fox', 'hare', 'pheasant', 'fish', 'badger', 'burrow',
  'eggs', 'tracks', 'scat',
  'arrow_n', 'arrow_ne', 'arrow_e', 'arrow_se', 'arrow_s', 'arrow_sw', 'arrow_w', 'arrow_nw',
  'custom',
  // Retired web ids. Production documents were migrated to the canonical ids
  // on 2026-09-17, but a document the migration missed, or one in another
  // environment, still draws its old artwork rather than the generic pin.
  'hunter', 'hunter_safe', 'hunter_safety', 'dog', 'dog_safety', 'animal',
  'animal_moose', 'animal_reddeer', 'animal_roedeer', 'animal_wildboar', 'animal_bear',
  'animal_fox', 'animal_hare', 'animal_pheasant', 'animal_fallowdeer', 'animal_fish',
  'animal_badger', 'animal_whitetaildeer', 'animal_reindeer',
  'home', 'bar', 'atel', 'water', 'observed', 'salt_rock', 'foderplats', 'nesting_place',
  'poo', 'eftersok', 'wolf_1day', 'wolf_new', 'wolf_old', 'svinpest', 'othergps', 'new_pin',
]);

const OVERRIDES: Partial<Record<PinKind, { bg?: string; nobg?: string }>> = {
  camera:           { bg: "map_pin_cam",                nobg: "map_pin_cam_nobg" },
  animal_reindeer:  { bg: "map_pin_animal_reindeer2",   nobg: "map_pin_animal_reindeer2_nobg" },
  othergps:         {                                   nobg: "map_pin_other_gps_nobg" },
  svinpest:         {                                   nobg: "map_pin_other_nobg" },
  // CLUB-186: canonical (mobile) animal ids resolve to the existing web
  // `map_pin_animal_*` assets — no asset renames, no broken references.
  moose:             { bg: "map_pin_animal_moose",           nobg: "map_pin_animal_moose_nobg" },
  red_deer:          { bg: "map_pin_animal_reddeer",         nobg: "map_pin_animal_reddeer_nobg" },
  roe_deer:          { bg: "map_pin_animal_roedeer",         nobg: "map_pin_animal_roedeer_nobg" },
  fallow_deer:       { bg: "map_pin_animal_fallowdeer",      nobg: "map_pin_animal_fallowdeer_nobg" },
  white_tailed_deer: { bg: "map_pin_animal_whitetaildeer",   nobg: "map_pin_animal_whitetaildeer_nobg" },
  reindeer:          { bg: "map_pin_animal_reindeer2",       nobg: "map_pin_animal_reindeer2_nobg" },
  wild_boar:         { bg: "map_pin_animal_wildboar",        nobg: "map_pin_animal_wildboar_nobg" },
  bear:              { bg: "map_pin_animal_bear",            nobg: "map_pin_animal_bear_nobg" },
  fox:               { bg: "map_pin_animal_fox",             nobg: "map_pin_animal_fox_nobg" },
  hare:              { bg: "map_pin_animal_hare",            nobg: "map_pin_animal_hare_nobg" },
  pheasant:          { bg: "map_pin_animal_pheasant",        nobg: "map_pin_animal_pheasant_nobg" },
  fish:              { bg: "map_pin_animal_fish",            nobg: "map_pin_animal_fish_nobg" },
  badger:            { bg: "map_pin_animal_badger",          nobg: "map_pin_animal_badger_nobg" },
  // CLUB-186: canonical (mobile) place ids reuse the existing legacy web assets.
  gathering_place:   { bg: "map_pin_home",                  nobg: "map_pin_home_nobg" },
  road_barrier:      { bg: "map_pin_bar",                   nobg: "map_pin_bar_nobg" },
  bait:              { bg: "map_pin_atel",                  nobg: "map_pin_atel_nobg" },
  eggs:              { bg: "map_pin_nesting_place",         nobg: "map_pin_nesting_place_nobg" },
  crossing:          { bg: "map_pin_water",                 nobg: "map_pin_water_nobg" },
  salt_stone:        { bg: "map_pin_salt_rock",             nobg: "map_pin_salt_rock_nobg" },
  feeding_grounds:   { bg: "map_pin_foderplats",            nobg: "map_pin_foderplats_nobg" },
  scat:              { bg: "map_pin_poo",                   nobg: "map_pin_poo_nobg" },
  // CLUB-525: a directional arrow annotates a heading rather than depicting a
  // thing, so it has no pointer tail and no separate round icon — the same
  // drawing serves both `withBackground` states (mobile ships that one file in
  // both folders). Pointing `nobg` at the bg filename keeps it a single asset
  // per direction instead of eight byte-identical duplicates.
  arrow_n:           { nobg: "map_pin_arrow_n" },
  arrow_ne:          { nobg: "map_pin_arrow_ne" },
  arrow_e:           { nobg: "map_pin_arrow_e" },
  arrow_se:          { nobg: "map_pin_arrow_se" },
  arrow_s:           { nobg: "map_pin_arrow_s" },
  arrow_sw:          { nobg: "map_pin_arrow_sw" },
  arrow_w:           { nobg: "map_pin_arrow_w" },
  arrow_nw:          { nobg: "map_pin_arrow_nw" },
  // `custom` has its own artwork (map_pin_custom / _nobg, ported from mobile):
  // a hollow teardrop with a plain disc for the user's symbol to sit on. It
  // used to borrow the `pin` drawing, whose own inner glyph then showed
  // through the symbol.
};

export function pinHref(kind: PinKind, variant: "bg" | "nobg"): string {
  const resolved = KNOWN_ART.has(kind) ? kind : "other";
  const ov = OVERRIDES[resolved];
  if (ov?.[variant]) return `/assets/${ov[variant]}.svg`;
  return variant === "nobg"
    ? `/assets/map_pin_${resolved}_nobg.svg`
    : `/assets/map_pin_${resolved}.svg`;
}

export function pinSrc(kind: PinKind): string {
  return pinHref(kind, "bg");
}

/**
 * The bare-icon asset for a pin type — what renders when the user turns pin
 * backgrounds off (`appearance.pinBackground`).
 *
 * CLUB-525: exported so the asset audit can assert BOTH `withBackground`
 * states. `pinSrc` alone only ever proved the teardrop variant exists, which is
 * how CLUB-491 shipped: a registry entry whose `_nobg` file is absent renders
 * fine for everyone with backgrounds on and 404s for everyone with them off.
 */
export function pinSrcNoBg(kind: PinKind): string {
  return pinHref(kind, "nobg");
}

/**
 * Pin asset for a given `withBackground` state — for the flat `<img>` surfaces
 * (pin picker, type selector) that render a pin type outside the map canvas.
 *
 * Those surfaces are choosing a pin the user is about to see ON the map, so
 * showing the backgrounded drawing while the map draws bare icons made the
 * picker disagree with the thing it was picking for.
 */
export function pinSrcFor(kind: PinKind, withBackground: boolean): string {
  return withBackground ? pinSrc(kind) : pinSrcNoBg(kind);
}

/**
 * CLUB-676: a pin's artwork at list size, for surfaces that name pins in rows
 * rather than drawing them on the map (the folder tree).
 *
 * Built on the same `useRecolouredPinHref` the map sprite uses, so a pin the
 * hunter coloured shows that colour in the list too. Drawn WITHOUT the teardrop
 * background, matching the app's own list rows — the teardrop is map furniture
 * that says "this is a position", and a list row is not a position.
 */
export function PinGlyph({
  kind,
  color,
  symbol,
  size = 22,
}: {
  kind: PinKind;
  color?: string;
  /** The glyph a `custom` pin draws in place of type artwork. */
  symbol?: string;
  size?: number;
}) {
  // Not every pin type has a `_nobg` file. The asset audit guarantees the pair
  // only for ids the pin-type catalog covers, and a pin can legitimately carry
  // a type outside it — `othergps`, what a Tracker/GPX import writes, has the
  // backgrounded drawing and no bare one.
  //
  // A missing asset does NOT 404 here: both the dev server and the production
  // nginx SPA serve index.html for an unmatched path, so the request comes back
  // 200 with HTML. Nothing throws, the recolour pass finds no fills and hands
  // back a data URI full of markup, and the row shows a broken-image icon. Only
  // the <img>'s own error event catches it — hence the fallback to the
  // backgrounded drawing, which every type has.
  const [bareFailed, setBareFailed] = useState(false);
  const href = useRecolouredPinHref(pinHref(kind, bareFailed ? "bg" : "nobg"), color, pinHref(kind, "bg"));
  if (kind === "custom") {
    // A bare custom pin is the symbol alone — no artwork — exactly as mobile
    // draws it: bold, in the pin's colour, with a white stroke as its border.
    return (
      <span aria-hidden="true" style={{ width: size, height: size, flexShrink: 0, display: "inline-flex" }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
          <CustomSymbolText symbol={symbol} color={color} box={size} cx={size / 2} cy={size / 2 + size * 0.02} />
        </svg>
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <img
        src={href}
        width={size}
        height={size}
        alt=""
        onError={() => setBareFailed(true)}
        style={{ display: "block" }}
      />
    </span>
  );
}

/**
 * The bare custom-pin glyph: the user's symbol, bold, in the pin's colour,
 * outlined in white — a border, not a background. Mobile's sizes: the font is
 * 0.71 and the stroke 0.08 of the box the pin is drawn in. Used by the map
 * sprite when backgrounds are off and by the list-row glyph.
 */
function CustomSymbolText({
  symbol,
  color,
  box,
  cx,
  cy,
}: {
  symbol: string | undefined;
  color: string | undefined;
  box: number;
  cx: number;
  cy: number;
}) {
  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'Open Sans', sans-serif"
      fontWeight={700}
      fontSize={box * 0.71}
      fill={color ?? pinDefaultColor("custom")}
      stroke="#fff"
      strokeWidth={box * 0.08}
      strokeLinejoin="round"
      paintOrder="stroke"
      pointerEvents="none"
    >
      {symbol || DEFAULT_CUSTOM_PIN_SYMBOL}
    </text>
  );
}

/**
 * CSS px to pull a pin marker's bottom edge up by, so the pin's location lands
 * where `pinAnchor` says it should.
 *
 * Every pin marker is a DOM element anchored `bottom` at the pin's location,
 * and `MapPinSprite` draws with its anchor point (28, 47) on that bottom edge —
 * right for a pin that stands on its tip. A pin that is centred on its location
 * instead (a bare icon, an arrow) is lifted by the distance from that point to
 * the middle of its drawing, as a negative bottom margin on the sprite: the
 * marker's box then ends at the drawing's middle while the sprite still paints
 * below it, and the name label above keeps its place. Without this every bare
 * icon on the web sat half its height above the spot it marks, where mobile
 * centres it.
 *
 * `scale` is the sprite's net on-screen scale (the Pin size setting).
 */
export function pinMarkerCentreLiftPx(kind: PinKind, withBackground: boolean, scale: number): number {
  if (pinAnchor(kind, withBackground) === 'bottom') return 0;
  // The image boxes `MapPinSprite` draws in, below: y 28 + 20.83 tall with the
  // background, y 27 + 20 tall without.
  const middle = withBackground ? 28 + 20.83 / 2 : 27 + 20 / 2;
  return (47 - middle) * scale;
}

export function MapPinSprite({
  kind,
  x,
  y,
  symbol,
  withBackground = true,
  selected = false,
  onMouseDown,
  onClick,
  cursor = "grab",
  scale = 1,
  color,
  opacity = 1,
  interactive = true,
}: {
  kind: PinKind;
  x: number;
  y: number;
  /** The glyph a `custom` pin draws in place of a type icon — an emoji, or a
      letter on pins made by older web versions. Ignored for every other kind,
      which has artwork of its own. */
  symbol?: string;
  withBackground?: boolean;
  selected?: boolean;
  onMouseDown?: (e: MouseEvent) => void;
  onClick?: (e: MouseEvent) => void;
  cursor?: string;
  scale?: number;
  color?: string;
  opacity?: number;
  /**
   * False while the map is in a mode that owns the next click — placing a pin,
   * drawing, editing vertices.
   *
   * This has to be a prop on the sprite: `pointer-events` is decided per
   * element, so a wrapping `<g style={{pointerEvents:'none'}}>` does NOT
   * suppress the hit circle below, which sets `pointer-events: all` itself. The
   * circle therefore stayed live during pin placement — it showed the grab
   * cursor, and its `onClick` called `stopPropagation()`, swallowing the canvas
   * click that would have dropped the new pin. The result was that a pin simply
   * could not be placed within ~18 units of an existing one.
   */
  interactive?: boolean;
}) {
  const dprScale = useDPRScale();
  const baseHref = pinHref(kind, withBackground ? "bg" : "nobg");
  const href = useRecolouredPinHref(baseHref, color, pinHref(kind, "bg"));
  const effectiveScale = scale * dprScale;

  return (
    <g
      transform={`translate(${x},${y}) scale(${effectiveScale}) translate(-28,-47)`}
      // Cursor is left unset when non-interactive so the canvas's own cursor
      // (crosshair while placing) shows through instead of this pin's grab hand.
      style={{ cursor: interactive ? cursor : undefined, opacity }}
      onMouseDown={interactive ? onMouseDown : undefined}
      onClick={
        interactive
          ? (e) => {
              e.stopPropagation();
              onClick?.(e);
            }
          : undefined
      }
    >
      <circle
        cx={28}
        cy={37}
        r={18}
        fill="transparent"
        pointerEvents={interactive ? "all" : "none"}
      />
      {selected && (
        <circle
          cx={28}
          cy={35}
          r={15}
          fill="none"
          stroke="rgba(0,0,0,0.45)"
          strokeWidth="2"
          strokeDasharray="3 2.5"
        />
      )}
      {kind === "custom" && !withBackground ? (
        // Backgrounds off: a custom pin is its symbol alone, as on mobile — no
        // artwork, bold, in the pin's colour, with a white stroke as border.
        <CustomSymbolText symbol={symbol} color={color} box={20} cx={28} cy={37} />
      ) : withBackground ? (
        <image href={href} x={18} y={28} width={20} height={20.83} />
      ) : (
        <image href={href} x={18} y={27} width={20} height={20} />
      )}
      {kind === "custom" && withBackground && (
        // Centred on the teardrop's head, white against the disc tinted with
        // the pin's own colour; an emoji carries its own colours and ignores
        // the fill. Mobile shows the default symbol when a pin has none.
        // pointerEvents="none" so the glyph never swallows a click meant for
        // the pin.
        <text
          x={28}
          y={36.5}
          textAnchor="middle"
          dominantBaseline="central"
          fontWeight={700}
          fontSize={11}
          fill="#fff"
          pointerEvents="none"
        >
          {symbol || DEFAULT_CUSTOM_PIN_SYMBOL}
        </text>
      )}
    </g>
  );
}
